/**
 * Trusted-device token storage. Web-only: it is held in a cookie next to the
 * session cookies, which `@tenant/core` cannot reach.
 *
 * The backend owns the lifetime, and the client is deliberately dumb about it:
 *
 *  - The token is issued by the verify endpoint only, and only when the user
 *    ticked "trust this device". A login that succeeds *via* a trusted device
 *    returns no token, so there is nothing to re-store on the login path.
 *  - It is never rotated. Rotating would kill the copy held by every other tab
 *    on the same device.
 *  - Server-side trust ends at midnight Monday NZ regardless of when it was
 *    earned. The cookie's own expiry is far longer on purpose — the browser
 *    cannot know when that Monday passed, so it keeps sending the token and
 *    lets the 428 tell it the token is dead. Shortening
 *    TWO_FACTOR_DEVICE_TOKEN_DAYS to match would not buy anything and would
 *    make users re-tick the box while their trust was still live.
 *
 * So: store on verify success, read on login, clear on 428. Nothing else.
 */
import {
  KEY_TWO_FACTOR_DEVICE_TOKEN,
  TWO_FACTOR_DEVICE_TOKEN_DAYS,
} from '../constants';
import {deleteCookie, getCookie, setCookie} from './cookie';

/**
 * Token proving this browser has already passed a code challenge, or null
 * when there is none.
 *
 * @returns {?string}
 */
export const getTwoFactorDeviceToken = () =>
  getCookie(KEY_TWO_FACTOR_DEVICE_TOKEN) || null;

/**
 * Remembers this browser for TWO_FACTOR_DEVICE_TOKEN_DAYS.
 *
 * @param {string} token
 * @returns {void}
 */
export const setTwoFactorDeviceToken = token => {
  const expiry = new Date(
    Date.now() + TWO_FACTOR_DEVICE_TOKEN_DAYS * 24 * 60 * 60 * 1000
  ).toUTCString();
  setCookie(KEY_TWO_FACTOR_DEVICE_TOKEN, token, expiry);
};

/**
 * Removes the trusted device token from this browser.
 *
 * @returns {void}
 */
export const forgetTwoFactorDeviceToken = () =>
  deleteCookie(KEY_TWO_FACTOR_DEVICE_TOKEN);
